import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { usePortalAuth } from '@/hooks/usePortalAuth';
import { logUnauthorizedAccessAttempt } from '@/lib/securityLogger';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiresAuth?: boolean;
  requiredRoles?: string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiresAuth = true,
  requiredRoles
}) => {
  const { user, loading, userRole } = useAuth();
  const { portalUser } = usePortalAuth();
  const location = useLocation();
  const [hasTimedOut, setHasTimedOut] = useState(false);

  // Failsafe timer - on timeout, DENY access if roles unknown
  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading || (requiredRoles && !userRole)) {
        console.warn('Loading timeout reached - denying access for security');
        setHasTimedOut(true);
      }
    }, 7000);

    return () => clearTimeout(timer);
  }, [loading, requiredRoles, userRole]);

  if (loading && !hasTimedOut) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Laden...</p>
        </div>
      </div>
    );
  }

  // Timeout reached but role still unknown - deny access
  if (hasTimedOut && requiredRoles && !userRole) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Check if this is a portal user trying to access CRM routes
  if (portalUser) {
    const redirectPath = portalUser.role === 'inspector' ? '/inspector' : '/client';
    return <Navigate to={redirectPath} replace />;
  }

  if (requiresAuth && !user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Check role requirements - STRICT deny if role doesn't match
  if (requiredRoles && requiredRoles.length > 0) {
    if (!userRole || !requiredRoles.includes(userRole)) {
      console.warn('[ProtectedRoute] Role mismatch - redirecting to auth', {
        path: location.pathname,
        requiredRoles,
        userRole: userRole || 'none',
        userId: user?.id
      });
      
      // Log unauthorized access attempt for CRM routes
      if (location.pathname.includes('/crm') && userRole === 'charging_user' && user?.id) {
        logUnauthorizedAccessAttempt(
          user.id,
          user.user_metadata?.tenant_id || null,
          {
            attempted_url: location.pathname,
            user_role: userRole || 'unknown',
            user_agent: navigator.userAgent,
            referrer: document.referrer,
            redirect_to: '/auth'
          }
        ).catch(console.error);
      }
      return <Navigate to="/auth" state={{ from: location }} replace />;
    }
  }

  return <>{children}</>;
};